import { useState, MouseEvent, useEffect } from 'react';
import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { title } from '../../../assets/styles/appStyles';
import Styles from '../../Locations/Styles/ListCustodiansStyles';
import { useNavigate, useParams } from 'react-router';
import { Add, CancelOutlined, SaveOutlined } from '@mui/icons-material';
import {
  cancelIcon,
  cancelIconText,
  iconContainer,
  saveIcon,
  saveIconText,
} from '../Styles/MainInformationTabStyles';
import usePostAssociations from '../../../hooks/Locations/edit/usePostAssociations';
import useAddAssociations from '../../../hooks/Locations/edit/useAddAssociations';
import Associations from './Associations';
import AssignEntityDialog from '../AssignEntityDialog';
import useUI from '../../../hooks/context/useUI';
import useSearchAssociations from '../../../hooks/associations/useSearchAssociations';
import { validateAssociationData } from '../../../utils/validationHelpers';

export default function AddAssociations() {
  const { id } = useParams();
  const { facilityData, getFacility } = useSearchAssociations();

  const [open, setOpen] = useState<boolean>(false);
  const handleCloseDialog = () => setOpen(false);
  const navigate = useNavigate();

  const [facilityName, setFacilityName] = useState<string>();
  const [parentEntityId, setParentEntityId] = useState<number>();
  const [associationData, setAssociationData] = useState<any>(null);
  const { showSnackbar } = useUI();

  const addClickHandler = (e: MouseEvent<any>) => {
    e.stopPropagation();
    setOpen(true);
  };

  const cancelClickHandler = () => {
    navigate(`/associations`);
    setFacilityName('');
  };

  const { associationsForm, providers, recordTypes } = useAddAssociations();
  const { edit, isSaving } = usePostAssociations(parentEntityId);

  const saveClicked = async () => {
    try {
      const validationErrors = validateAssociationData(associationData);
      if (validationErrors.length > 0) {
        showSnackbar(validationErrors, 'error');
        return;
      }
      const result = await edit(associationData);
      console.log('Save successful:', result);
    } catch (err) {
      console.error('Save failed:', err);
    }
  };

  const onSelectFacility = (item: any) => {
    if (item?.associationCount === 0) {
      setParentEntityId(item.id);
      setFacilityName(item.name);
    } else {
      navigate(`/associations/edit/${item.id}`);
    }
    setOpen(false);
  };

  useEffect(() => {
    if (id !== undefined && parentEntityId === undefined) {
      getFacility(id);
    }
  }, [id]);

  useEffect(() => {
    if (facilityData && parentEntityId === undefined) {
      setParentEntityId(facilityData.id);
      setFacilityName(facilityData.name);
    }
  }, [facilityData]);

  return (
    <Box p={2}>
      <Typography
        sx={{
          ...Styles.headerTitleStyles,
          mb: 3,
        }}
      >
        Association Management
      </Typography>
      {!facilityName && (
        <Button
          variant="outlined"
          onClick={addClickHandler}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 3,
          }}
        >
          <Add />
          <Typography>Add Facility</Typography>
        </Button>
      )}
      {facilityName && (
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography
            sx={{
              ...title,
              color: 'orange',
              fontWeight: 600,
              fontSize: '18px',
            }}
          >
            {facilityName}
          </Typography>
          {
            <Grid item>
              <Grid container spacing={2}>
                <Grid item>
                  {isSaving ? (
                    <CircularProgress />
                  ) : (
                    <Box sx={iconContainer} onClick={cancelClickHandler}>
                      <CancelOutlined sx={cancelIcon} />
                      <Typography sx={cancelIconText}>Cancel</Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item>
                  {isSaving ? (
                    <CircularProgress />
                  ) : (
                    <Box sx={iconContainer} onClick={saveClicked}>
                      <SaveOutlined sx={saveIcon} />
                      <Typography sx={saveIconText}>Save</Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Grid>
          }
        </Grid>
      )}
      <AssignEntityDialog
        open={open}
        handleClose={handleCloseDialog}
        onItemSelected={(item) => onSelectFacility(item)}
        associatedEntities={[]}
        entityLabel={'Facility'}
      />
      {associationsForm ? (
        <Grid container my={2}>
          <Grid item xs={12}>
            <form
              name={'associations'}
              onSubmit={associationsForm.handleSubmit}
            >
              <Associations
                parentEntityId={parentEntityId}
                form={associationsForm}
                supportedRecordTypes={recordTypes}
                providers={providers}
                associationData={associationData}
                setAssociationData={setAssociationData}
              />
            </form>
          </Grid>
        </Grid>
      ) : (
        ''
      )}
    </Box>
  );
}
